// Shared pre-order gate + weekend/holiday date math, ported verbatim
// from Atlas's dateHelpers.js + updateLineItemProperties.js. Used by
// blocks/bp-preorder-line.liquid (PDP) and snippets/bp-cart-line-item.liquid
// (cart drawer + cart page) so both surfaces show/hide and format the
// "Expected Ship Date" line identically instead of drifting.
window.bpPreorder = (function () {
  // Atlas hardcodes shippingInventoryThreshold = 1
  // (updateLineItemProperties.js:31) — same here, ignored as a schema
  // knob to keep the gate deterministic across environments.
  const THRESHOLD = 1;

  const FIXED_HOLIDAYS = ['1/1', '5/30', '7/4', '12/24', '12/25', '12/31'];
  // "month,week,day" — 0-indexed month, 0-indexed week-of-month, ISO
  // day-of-week (0=Sun … 6=Sat). Copied from Atlas's flexibleHolidays
  // object → flexibleHolidaysVal array.
  const FLEXIBLE_HOLIDAYS = [
    '0,2,1', // MLK Day
    '1,2,1', // Presidents Day
    '4,-1,1', // Memorial Day
    '8,0,1', // Labor Day
    '10,3,4', // Thanksgiving
    '10,3,5', // Day after Thanksgiving
  ];

  function bumpWeekend(d) {
    // Atlas: `date.getDay() % 6 == 0` — Sat (6) or Sun (0).
    if (d.getDay() % 6 === 0) {
      d.setDate(d.getDate() + (d.getDay() === 6 ? 2 : 1));
    }
  }

  function moveHoliday(date) {
    let month = date.getMonth();
    let day = date.getDate();
    let dayOfWeek = date.getDay();
    let week = Math.floor((day - 1) / 7);
    let dateStr = (month + 1) + '/' + day;
    let mwd = month + ',' + week + ',' + dayOfWeek;
    while (FLEXIBLE_HOLIDAYS.includes(mwd) || FIXED_HOLIDAYS.includes(dateStr)) {
      date.setDate(date.getDate() + 1);
      bumpWeekend(date);
      month = date.getMonth();
      day = date.getDate();
      dayOfWeek = date.getDay();
      const dayOffset = new Date(date.getFullYear(), month, 1).getDay();
      week = Math.floor((day - dayOffset) / 7);
      mwd = month + ',' + week + ',' + dayOfWeek;
      dateStr = (month + 1) + '/' + day;
    }
    return date;
  }

  function skipWeekendsHol(raw) {
    if (!raw) return null;
    if (raw == 0) return null; // Atlas uses loose equality here.
    let d = new Date(raw);
    // Past dates aren't shown (dateHelpers.js:56).
    if (d < new Date()) return null;
    try {
      d = new Date(raw); // Atlas re-parses here — keep parity.
      bumpWeekend(d);
      d = moveHoliday(d);
    } catch (e) {
      console.log(e);
    }
    return d;
  }

  // Display format per Atlas product-main.js:104 —
  //   toLocaleDateString("en-US", { year, month:'long', day:'2-digit' })
  // → "July 30, 2026". No weekday.
  function formatDate(raw, originalOverride) {
    if (originalOverride) return originalOverride;
    const d = skipWeekendsHol(raw);
    if (!d) return null;
    try {
      return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: '2-digit' });
    } catch (_) {
      return d.toDateString();
    }
  }

  // All-gates check — mirrors Atlas's `displayExpectedShipDate` in
  // updateLineItemProperties.js:55-61, MINUS the shipDateSwitch gate:
  //   hasDate (dcDate present && !== "0")
  //   isPreOrder
  //   variantInboundInventory > 0
  //   variantInventoryAvailable - shippingInventoryThreshold <=
  //      variantInboundInventory
  //
  // The `shipDateSwitch !== "0"` gate was REMOVED (AN 0813): the new site
  // must not use bp_shipdate_inbound to decide the ship-date display, because
  // the ERP set it to "0" on qualifying variants (e.g. XXS) and wrongly hid
  // the date. bp-preorder-gates.liquid no longer emits `switchActive`.
  //
  // The `hasCurrentInventory` (currentInv > inboundInv) guard was REMOVED
  // (AN 0926): Shopify's variant.inventory_quantity already includes on-order
  // units baked into on-hand by the Celigo flow, so currentInv is always
  // approximately equal to inboundInv even when no physical stock exists in
  // the DC. The guard fired for variants where the difference was exactly 1
  // (within THRESHOLD) and incorrectly hid the ship date. The THRESHOLD check
  // below is the authoritative gate and handles this correctly on its own.
  function shouldShow(entry) {
    if (!entry) return false;
    if (!entry.dcDate || entry.dcDate === '0') return false;
    if (!entry.isPreOrder) return false;
    if (entry.inboundInv <= 0) return false;
    if (entry.currentInv - THRESHOLD > entry.inboundInv) return false;
    return true;
  }

  // Cart drawer + cart page: recompute the line live from
  // item.variant metafields (rendered server-side into
  // script[data-bp-cart-ship-gate="{key}"] by
  // snippets/bp-cart-line-item.liquid) rather than trusting whatever
  // line-item property was captured at add-to-cart time — mirrors
  // Atlas's LineItemCard.js, which re-fetches variant metafields via
  // GraphQL on every cart render instead of reading cart.items[].properties.
  // Falls back to the add-to-cart-time LIP (data-lip-fallback) only if
  // the live recompute has nothing to show, matching Atlas's fallback
  // order (LineItemCard.js:73-84).
  function applyCartShipDates(root) {
    const scope = root || document;
    const gates = new Map();
    document.querySelectorAll('script[data-bp-cart-ship-gate]').forEach((s) => {
      const key = s.getAttribute('data-bp-cart-ship-gate');
      try {
        gates.set(key, JSON.parse(s.textContent || '{}'));
      } catch (e) {
        console.log('[bp-preorder] bad cart gate JSON', key, e);
      }
    });
    scope.querySelectorAll('[data-bp-cart-line-ship-date]').forEach((el) => {
      const key = el.getAttribute('data-bp-cart-line-ship-date');
      const entry = gates.get(key);
      let formatted = null;
      if (shouldShow(entry)) {
        formatted = formatDate(entry.dcDate, el.getAttribute('data-original-override'));
      }
      if (!formatted) {
        formatted = el.getAttribute('data-lip-fallback') || null;
      }
      if (formatted) {
        el.textContent = 'EXPECTED SHIP DATE: ' + formatted.toUpperCase();
        el.hidden = false;
      } else {
        el.textContent = '';
        el.hidden = true;
      }
    });
  }

  const runInitial = () => applyCartShipDates(document);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runInitial);
  } else {
    runInitial();
  }
  // bp-cart-drawer.liquid dispatches this after syncItemsList() swaps in
  // fresh line-item HTML via innerHTML — which does not execute the
  // freshly-injected <script> tags, so re-applying explicitly here is
  // required, not optional.
  document.addEventListener('bp:cart-lines:rendered', () => applyCartShipDates(document));

  return { shouldShow, formatDate, skipWeekendsHol, applyCartShipDates };
})();
